import React from "react"
import { InlineWidget, CalendlyEventData } from "react-calendly"
import { useNavigate } from "react-router-dom"
import { useTypedSelector } from "../app/store"
import { useUpdateOnboardingAppointmentMutation } from "../app/services/meseeknaApiV2"
import LogoFull from "./logo-full"
import { useOrganizationRecovery } from "../hooks/useOrganizationRecovery"

const OnboardingCalendlyRedirect: React.FC = () => {
  const navigate = useNavigate()
  const { organization, user } = useTypedSelector((state) => state.authSlice)
  const [isScheduling, setIsScheduling] = React.useState(false)
  const [updateAppointment] = useUpdateOnboardingAppointmentMutation()

  // Ensure we have organization ID
  useOrganizationRecovery()

  // Get effective organization ID from multiple sources
  const effectiveOrgId =
    organization?.id || user?.organizationId?.toString() || ""

  const handleAppointmentScheduled = async (e: { data: CalendlyEventData }) => {
    try {
      setIsScheduling(true)
      console.log("Appointment scheduled:", e.data)

      // Update the organization's onboarding status using RTK Query
      if (effectiveOrgId) {
        console.log(
          "Updating appointment status for organization:",
          effectiveOrgId,
        )
        await updateAppointment({
          organizationId: effectiveOrgId,
          scheduled: true,
        })
      } else {
        console.error("Cannot update appointment - missing organization ID")
      }

      // Redirect back to the original page the user was trying to access
      const returnUrl =
        sessionStorage.getItem("redirectAfterOnboarding") ||
        "/dashboard/simulations"
      sessionStorage.removeItem("redirectAfterOnboarding")

      // Navigate back to the original URL
      navigate(returnUrl)
    } catch (error) {
      console.error("Error updating onboarding appointment status:", error)
      setIsScheduling(false)
    }
  }

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-col items-center justify-start">
      <header className="w-full py-4 px-8 bg-white shadow-sm flex items-center justify-between">
        <LogoFull
          dimensions="/v3/assets/meseekna-logo@2x.png"
          logoWrapWidth="160px"
          logoWrapHeight="28px"
          image1Width="160px"
          image1Height="28px"
        />
      </header>

      <main className="w-full max-w-4xl flex-1 flex flex-col items-center p-8">
        <div className="w-full mb-8 text-center">
          <h1 className="text-2xl font-semibold mb-4">
            Complete Your Onboarding
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mb-2">
            Before you continue, please schedule your onboarding call with our
            team. This will help you get the most out of our platform and ensure
            you&apos;re set up for success.
          </p>
          {isScheduling && (
            <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 mt-4 rounded-md">
              <p className="flex items-center">
                <svg
                  className="animate-spin h-5 w-5 mr-3 text-blue-600"
                  viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Scheduling your appointment... You&apos;ll be redirected when
                complete.
              </p>
            </div>
          )}
        </div>

        <div className="w-full bg-white rounded-lg shadow-md p-6 flex-1 min-h-0">
          {process.env.REACT_APP_CALENDLY_URL ? (
            <InlineWidget
              url={process.env.REACT_APP_CALENDLY_URL}
              styles={{ height: "100%", minHeight: "500px" }}
              pageSettings={{
                hideEventTypeDetails: true,
                hideLandingPageDetails: true,
              }}
              onEventScheduled={handleAppointmentScheduled}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-red-500 mb-4">⚠️</div>
              <h2 className="text-xl font-bold mb-2">
                Calendly URL Not Configured
              </h2>
              <p className="text-center mb-4">
                The Calendly URL is not configured in the environment settings.
              </p>
              <p className="text-sm text-gray-500">
                For development purposes, you can click below to simulate
                scheduling:
              </p>
              <button
                onClick={() =>
                  handleAppointmentScheduled({ data: {} as CalendlyEventData })
                }
                className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                Simulate Appointment Scheduled
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default OnboardingCalendlyRedirect
